import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, UserPlus, Upload } from 'lucide-react';
import { api } from '../../api.js';
import { Card, CardHeader, Button, Input, Select, Badge, EmptyState, PageHeader, Spinner } from '../../components/ui.jsx';

export default function UsersSetup() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['users-list-setup'], queryFn: () => api.listUsers() });
  const { data: groupData } = useQuery({ queryKey: ['groups'], queryFn: api.listGroups });
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', job_title: '', role: 'individual', group_ids: [] });
  const [bulkText, setBulkText] = useState('');
  const [showBulk, setShowBulk] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: (body) => api.inviteUser(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users-list-setup'] }); setInviting(false); setForm({ email: '', full_name: '', job_title: '', role: 'individual', group_ids: [] }); }
  });

  const bulkMutation = useMutation({
    mutationFn: (rows) => api.bulkInviteUsers({ rows }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users-list-setup'] }); setShowBulk(false); setBulkText(''); }
  });

  const deactivateMutation = useMutation({
    mutationFn: (id) => api.deactivateUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users-list-setup'] })
  });

  const handleBulkSubmit = () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    const rows = lines.map(line => {
      const [email, full_name, job_title] = line.split(',').map(s => s?.trim());
      return { email, full_name, job_title };
    }).filter(r => r.email && r.full_name);
    if (rows.length > 0) bulkMutation.mutate(rows);
  };

  if (isLoading) return <div className="min-h-[40vh] grid place-items-center"><Spinner className="w-8 h-8 text-primary-600" /></div>;

  const users = data?.users || [];
  const groups = groupData?.groups || [];

  return (
    <>
      <PageHeader title="Users" description="Invite people to your workspace and manage their roles."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowBulk(true)}><Upload className="w-4 h-4 mr-1.5" />Bulk import</Button>
            <Button onClick={() => setInviting(true)}><UserPlus className="w-4 h-4 mr-1.5" />Invite user</Button>
          </div>
        } />

      {inviting ? (
        <Card className="mb-6">
          <CardHeader title="Invite a user" />
          <form onSubmit={(e) => { e.preventDefault(); inviteMutation.mutate(form); }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Email" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <Input label="Full name" required value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            <Input label="Job title" value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} />
            <Select label="Role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              options={[
                { value: 'individual', label: 'Individual' },
                { value: 'supervisor', label: 'Supervisor (sees direct reports)' },
                { value: 'group_admin', label: 'Group admin (sees a group)' },
                { value: 'super_user', label: 'Super-user (full access)' }
              ]} />
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" loading={inviteMutation.isPending}>Send invitation</Button>
              <Button type="button" variant="ghost" onClick={() => setInviting(false)}>Cancel</Button>
            </div>
            {inviteMutation.error ? <p className="md:col-span-2 text-sm text-danger">{inviteMutation.error.message}</p> : null}
          </form>
        </Card>
      ) : null}

      {showBulk ? (
        <Card className="mb-6">
          <CardHeader title="Bulk import" subtitle="One user per line. Format: email, full name, job title (optional)" />
          <textarea className="input min-h-[180px] font-mono text-xs" value={bulkText} onChange={e => setBulkText(e.target.value)}
            placeholder="alex@example.com, Alex Tan, Sales Manager&#10;sam@example.com, Sam Patel, Account Executive" />
          <div className="mt-4 flex gap-2">
            <Button onClick={handleBulkSubmit} loading={bulkMutation.isPending}>Send invitations</Button>
            <Button variant="ghost" onClick={() => setShowBulk(false)}>Cancel</Button>
          </div>
          {bulkMutation.data ? (
            <p className="mt-3 text-sm text-ink-600">Created {bulkMutation.data.created?.length || 0}, skipped {bulkMutation.data.skipped?.length || 0}.</p>
          ) : null}
        </Card>
      ) : null}

      {users.length === 0 ? (
        <EmptyState icon={UserPlus} title="No users yet" description="Invite your first user to get started." />
      ) : (
        <Card padding={false}>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-ink-500 border-b border-ink-200">
              <tr>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-ink-100 hover:bg-ink-50/50">
                  <td className="py-3 px-4 font-medium text-ink-900">{u.full_name}</td>
                  <td className="py-3 px-4 text-ink-600">{u.email}</td>
                  <td className="py-3 px-4"><Badge variant={u.status === 'active' ? 'green' : u.status === 'invited' ? 'amber' : 'grey'}>{u.status}</Badge></td>
                  <td className="text-right py-3 px-4 text-ink-500 text-xs">{new Date(u.created_at).toLocaleDateString('en-AU')}</td>
                  <td className="py-3 px-2">
                    {u.status !== 'deactivated' ? (
                      <button onClick={() => { if (confirm(`Deactivate ${u.full_name}?`)) deactivateMutation.mutate(u.id); }}
                        className="text-xs text-ink-500 hover:text-danger">Deactivate</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
